import {
  countyReportFromPath,
  countyReportTitle,
  renderCountyStormReport,
  type CountyStormSnapshot,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storm-poll`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export { countyReportFromPath };

type Loaded =
  | { state: "loading" }
  | { state: "open"; html: string }
  | { state: "closed"; message: string };

/**
 * `/storms/{county}/{date}` — the page a partner forwards.
 *
 * The same arrangement as the adjuster's storm report at `/s/<token>`, minus
 * the token: row level security shows an unauthenticated visitor nothing at
 * all, so the `storm-poll` function is the only thing that can turn a county
 * and a date into a page. There is no credential because there is nothing
 * private on it — every fact came from a government feed anybody can query.
 *
 * Written into an iframe rather than into this page, for the same reason the
 * storm report is: it is a complete standalone document with its own
 * stylesheet, built to be printed and forwarded, and dropping it into the app's
 * DOM would put two stylesheets in the same room.
 */
export function CountyStormPage({
  county,
  date,
}: {
  county: "shelby" | "jefferson";
  date: string;
}) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(FUNCTION_URL, {
          method: "POST",
          headers: {
            apikey: ANON_KEY,
            Authorization: `Bearer ${ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ action: "report", county, date }),
        });
        const body = (await response.json().catch(() => null)) as {
          event?: CountyStormSnapshot;
          error?: string;
        } | null;
        if (cancelled) return;

        if (response.ok && body?.event) {
          setLoaded({
            state: "open",
            html: renderCountyStormReport(body.event),
          });
          document.title = countyReportTitle(body.event);
        } else {
          setLoaded({
            state: "closed",
            message:
              body?.error ?? "There is no report for that county on that date.",
          });
        }
      } catch {
        if (!cancelled) {
          setLoaded({
            state: "closed",
            message: "This report could not be loaded. Try again in a moment.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [county, date]);

  if (loaded.state === "loading") {
    return (
      <div style={SHELL}>
        <p style={NOTE}>Loading…</p>
      </div>
    );
  }

  if (loaded.state === "closed") {
    return (
      <div style={SHELL}>
        <p style={NOTE}>{loaded.message}</p>
      </div>
    );
  }

  return (
    <iframe
      title="County storm report"
      srcDoc={loaded.html}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      }}
    />
  );
}

/* The two states before the document exists. Pinned light, like the document
   itself — a stranger's phone in dark mode should not decide what this looks
   like. */
const SHELL: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f4f6fa",
  colorScheme: "light",
};

const NOTE: React.CSSProperties = {
  maxWidth: 420,
  padding: 24,
  textAlign: "center",
  color: "#6b7688",
  font: '16px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};
